import {useEffect, useState} from "react";
import {StmStateDto} from "../../common/dto/stm-state-dto";
import {ACTION_PROMPT, MESSAGE_PROMPT} from "../../common/const/settings-type";
import TextBox from "../../../../components/forms/text-box";
import TextArea from "../../../../components/forms/text-area";
import SettingTip from "../../common/components/setting-tip";
import { MdOutlineHideImage } from "react-icons/md";
import { Storage } from 'aws-amplify';
import {useAppInfo} from "../../../../helpers/hooks/common-hook";
import {useParams} from "react-router";
import {createUniqueId} from "../../../../helpers/utils/object-util";
import Message from "../../../../components/toast-message";
import FormLoader from "../../../../components/form-loader/form-loader";
import Button from "../../../../components/button/button";
import {fileUpload, uploadUrlGet, viewUrlGet} from "../../../../services/model/journey-service";

export default function ImageMessageSettings({name, onChangeData, savedSettings}) {
    const { appId } = useAppInfo();
    const { journeyId } = useParams();

    const [formData, setFormData] = useState(savedSettings);

    const [imageUrlLoadingState, setImageUrlLoading] = useState({
        processing: false,
        success: false,
        failed: false,
        message: '',
    });

    const [imageUploadState, setImageUploadState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: '',
    });

    const [displayImageUrl, setDisplayImageUrl] = useState('');
    const [imageSource, setImageSource] = useState(
        savedSettings?.imageUrl ? 'url' : 'upload'
    ); // 'upload' or 'url'

    const [fileObj, setFileObj] = useState(null);
    const [fileUploadUrl, setFileUploadUrl] = useState(null);
    const [fileKey, setFileKey] = useState(null);

    const updateImageUploadState = (processing, success, failed, message) => {
        setImageUploadState({processing, success, failed, message})
    }

    const updateImageUrlLoading = (processing, success, failed, message) => {
        setImageUrlLoading({processing, success, failed, message})
    }

    // Set display image URL on initial load if imageUrl exists
    useEffect(() => {
        if (savedSettings?.imageUrl) {
            setDisplayImageUrl(savedSettings.imageUrl);
        }
    }, []);

    useEffect(() =>  {
        formData.stateType = MESSAGE_PROMPT
        onChangeData(formData);

        if (formData.fileKey && imageSource === 'upload') {
            genViewUrl()
        }

    }, [formData]);

    useEffect(() =>  {
        if (fileObj) {
            // set for image preview
            const reader = new FileReader();
            reader.onload = (event) => {
                setDisplayImageUrl(event.target.result);
            };
            reader.readAsDataURL(fileObj);
            // end set for image preview

            genUploadUrl()
        }
    }, [fileObj]);

    useEffect(() => {
        if (fileUploadUrl && fileObj) {
            handleUploadImage();
        }
    }, [fileUploadUrl]);

    const handleChange = event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        const newFormData = {...formData}
        newFormData[targetName] = targetValue;
        setFormData(newFormData);
    };

    const handleImageSourceChange = (source) => {
        setImageSource(source);
        // Clear the other source's data when switching
        if (source === 'url') {
            setFileObj(null);
            setFileKey(null);
            setFileUploadUrl(null);
            const newFormData = {...formData};
            delete newFormData.fileKey;
            setFormData(newFormData);
        } else {
            const newFormData = {...formData};
            delete newFormData.imageUrl;
            setFormData(newFormData);
            setDisplayImageUrl('');
        }
    };

    const handleImageUrlChange = (event) => {
        const url = event.target.value;
        const newFormData = {...formData};
        newFormData.imageUrl = url;
        setFormData(newFormData);
        setDisplayImageUrl(url);
    };

    const handleSelectFile = async (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            setFileObj(file)
        }
    }

    const genUploadUrl = async () => {
        try {
            updateImageUrlLoading(true, false, false)
            const fileExt = fileObj.name.split('.').pop();
            const resp = await uploadUrlGet(appId, journeyId, {
                fileExt
            })
            setFileUploadUrl(resp.uploadUrl);
            setFileKey(resp.fileKey);
            updateImageUrlLoading(false, true, false)
        } catch (e) {
            updateImageUrlLoading(false, false, true, 'Unable to select image')
        }
    }

    const genViewUrl = async () => {
        try {
            updateImageUrlLoading(true, false, false)
            const resp = await viewUrlGet(appId, journeyId, {
                fileKey: formData.fileKey
            })
            setDisplayImageUrl(resp.viewUrl);
            updateImageUrlLoading(false, true, false)
        } catch (e) {
            updateImageUrlLoading(false, false, true, 'Unable to select image')
        }
    }

    const handleUploadImage = async () => {
        try {
            if (!fileUploadUrl) {
                return
            }

            updateImageUploadState(true, false, false)
            await fileUpload(fileUploadUrl, fileObj);
            
            // Update formData with fileKey and remove imageUrl if it exists
            const newFormData = {...formData};
            newFormData.fileKey = fileKey;
            delete newFormData.imageUrl;
            setFormData(newFormData);
            
            setFileKey(null)
            setFileObj(null)
            updateImageUploadState(false, true, false, 'Successfully uploaded image')
        } catch (err) {
            updateImageUploadState(false, false, true, 'Unable to upload image');
        }
    }

    return (
        <div className="flex flex-col gap-2">
            {(!imageUploadState.processing && imageUploadState.failed && imageUploadState.message) &&
                <Message text={imageUploadState.message} type="failure"/>
            }

            {(!imageUploadState.processing && imageUploadState.success && imageUploadState.message) &&
                <Message text={imageUploadState.message} type="success"/>
            }

            <SettingTip text="Image message sent to a user during an conversation"/>

            <TextBox
                label="Name"
                type="text"
                name="name"
                placeholder="State name"
                value={formData.name}
                onChange={handleChange}
            />

            <TextArea
                label="Caption"
                name="caption"
                placeholder="Enter image caption"
                value={formData.caption || ""}
                onChange={handleChange}
                rows={4}
            />

            <div className="pt-2">
                <label className="block mb-2 text-sm font-medium text-gray-900">
                    Image Source
                </label>
                <div className="flex gap-4 mb-4">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="imageSource"
                            value="upload"
                            checked={imageSource === 'upload'}
                            onChange={() => handleImageSourceChange('upload')}
                            className="mr-2"
                        />
                        <span className="text-sm text-gray-900">Upload File</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="imageSource"
                            value="url"
                            checked={imageSource === 'url'}
                            onChange={() => handleImageSourceChange('url')}
                            className="mr-2"
                        />
                        <span className="text-sm text-gray-900">Image URL</span>
                    </label>
                </div>
            </div>

            {imageSource === 'url' && (
                <div className="pt-2">
                    <TextBox
                        label="Image URL"
                        type="text"
                        name="imageUrl"
                        placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                        value={formData.imageUrl || ''}
                        onChange={handleImageUrlChange}
                    />
                </div>
            )}

            {imageSource === 'upload' && <div className="pt-2">
                <label className="block mb-2 text-sm font-medium text-gray-900"
                       htmlFor="fileContainer">
                    <span>Upload Image ( png , jpg )</span>
                </label>

                <input
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                    aria-describedby="user_avatar_help"
                    id="fileContainer"
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={handleSelectFile}
                />

            </div>}

            {imageUrlLoadingState.processing?
                <div className="pt-8 flex flex-row justify-center items-center">
                    <div className="flex-1 grid grid-cols-3 gap-5">
                        <div></div>
                        <div
                            className="border border-gray-300 dark-border shadow rounded-md p-4 max-w-sm w-full mx-auto">
                            <div className="animate-pulse flex space-x-4">
                                <div className="flex-1 space-y-6 py-1">
                                    <div className="h-2 bg-slate-200 rounded"></div>
                                    <div className="h-2 bg-slate-200 rounded"></div>
                                    <div className="h-2 bg-slate-200 rounded"></div>
                                </div>
                            </div>
                        </div>
                        <div></div>
                    </div>
                </div>
                :<div className="pt-4 flex flex-row justify-center">
                    {displayImageUrl?
                        <img className="h-36 w-36" src={displayImageUrl} alt="" />
                        :<MdOutlineHideImage className="h-36 w-36"/>}
                </div>}

            {(fileObj && fileKey && imageUploadState.processing) &&
                <div className="flex flex-row justify-center pt-4">
                    <Button
                        loading={true}
                        color="default"
                        type="button"
                        text="Uploading"
                    />
                </div>
            }

        </div>
    )
}
